import React from "react";
import PropTypes from "prop-types";
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import Alert from "react-bootstrap/Alert";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Redirect } from "react-router";
import { formatCurrency } from "@brazilian-utils/brazilian-utils";
import { useGenericAPI } from "../../hooks/useGenericAPI";
import { usePaymentStatusPolling } from "../../hooks/usePaymentStatusPolling";
import { InlineLoading, AllPaidNotice } from "..";
import { formatDateBR } from "../utils/dateUtils";
import { useTranslation } from "react-i18next";
import { parseApiErrors } from "../utils/apiErrors";

const MAX_ATTEMPTS = 10;
const RETRY_DELAY_MS = 1000;

export const PixForm = ({ payment, referenceId }) => {
  const { payment: data } = payment;
  const { t } = useTranslation();
  const [showCopy, setShowCopy] = React.useState(false);
  const [failedAttempts, setFailedAttempts] = React.useState(0);

  const installment = React.useMemo(
    () =>
      data.installments_from_gateway.find(i =>
        ["pending", "overdue"].includes(i.status),
      ),
    [data],
  );

  const { getRequest, loading, data: pix, error } = useGenericAPI(`pix/`);
  const paid = usePaymentStatusPolling(
    data.gateway_id,
    installment?.gateway_id,
  );

  const allPaid = React.useMemo(
    () =>
      data.installments_from_gateway.filter(i => ["paid"].includes(i.status))
        .length === data.installments_from_gateway.length,
    [data],
  );

  const retriesExhausted = failedAttempts >= MAX_ATTEMPTS;

  React.useEffect(
    () => {
      if (allPaid || retriesExhausted) {
        return;
      }

      getRequest(installment?.gateway_id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [installment?.gateway_id, allPaid, failedAttempts],
  );

  React.useEffect(() => {
    if (!error) {
      return;
    }

    const timer = setTimeout(
      () => setFailedAttempts(count => count + 1),
      RETRY_DELAY_MS,
    );

    return () => clearTimeout(timer);
  }, [error]);

  if (paid) {
    return (
      <Redirect
        to={`/payments/${data.gateway_id}/success?reference_id=${
          referenceId || "NOT_FOUND"
        }&billing_type=PIX`}
      />
    );
  }

  if (allPaid) {
    return <AllPaidNotice />;
  }

  if (retriesExhausted) {
    const apiMessage = parseApiErrors(error?.data).global;

    return (
      <Alert variant="danger" className="mt-3">
        <p className="mb-0">{t("pix-nao-carregado")}</p>
        {apiMessage && <small>{apiMessage}</small>}
      </Alert>
    );
  }

  if (loading || !data || !pix) {
    return <InlineLoading title={`${t("carregando-seu-pix")}…`} />;
  }

  const dueDate = formatDateBR(installment.due_date);

  const copyPix = () => {
    if (!pix) {
      return null;
    }

    setShowCopy(true);
  };

  return (
    <div>
      <Card className="mt-3">
        <Card.Body>
          <Card.Title as="h5">
            {t("pagar-x-vencimento-x", {
              description: installment.description,
              dueDate,
            })}
          </Card.Title>

          <p>{t("como-pagar-via-qr-code")}</p>

          <Container>
            <Row>
              <Col className="d-flex align-items-center justify-content-center">
                <img
                  className="js-pix-qr-code mb-3"
                  height="240px"
                  width="240px"
                  src={`data:image/jpeg;base64,${pix.encodedImage}`}
                  alt={t("qr-code-pix")}
                ></img>
              </Col>

              <Col className="d-flex align-items-center justify-content-center flex-column">
                <p className="fs-3 text-success">
                  <strong>
                    {t("valor")}: R$ {formatCurrency(installment.total)}
                  </strong>
                </p>

                <img
                  src="https://s3-sa-east-1.amazonaws.com/asaas-static/images/invoice/site-seguro.png"
                  alt={t("imagem-informando-site-seguro")}
                />

                <p className="pt-3 text-">
                  {t("email-com-instrucoes-do-pagamento")}
                </p>
              </Col>
            </Row>
          </Container>

          <h5 className="mt-3">{t("copie-e-cole-se-preferir-pix")}</h5>

          <InputGroup className="mb-2">
            <Form.Control readOnly value={pix.payload} isValid={showCopy} />
            <CopyToClipboard onCopy={copyPix} text={pix.payload}>
              <Button type="button" variant="dark" id="button-addon2">
                {showCopy ? `${t("codigo-copiado")}!` : t("copiar-codigo-pix")}
              </Button>
            </CopyToClipboard>
          </InputGroup>
        </Card.Body>
      </Card>
    </div>
  );
};

PixForm.propTypes = {
  data: PropTypes.object,
};

export default PixForm;
